import { useQuery } from 'react-query';
import { useAuth } from '../auth/AuthProvider';
import { InsightDashboard, Subscriptions } from '../utils/interfaces';
import { getRequestHeaders, vake_api_server_url } from './helpers';

export const useNorwayTransshipmentInsight = (nDays: string, country: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/recent_vessel_events/transshipments_in_aoi?n_days=${nDays}&case_name=${country}`;
    return useQuery<InsightDashboard[]>(
        ['nor_transshipment', nDays, country],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const useDarkVesselInsightInNorway = (nDays: string, country: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/recent_vessel_events/confident_dark_vessel_in_aoi?n_days=${nDays}&case_name=${country}`;
    return useQuery<InsightDashboard[]>(
        ['dark_vessel_nor', nDays, country],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const useSanctionedVesselInsightInNorway = (nDays: string, country: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/recent_vessel_events/sanctioned_vessel_verification_in_aoi?n_days=${nDays}&case_name=${country}`;
    return useQuery<InsightDashboard[]>(
        ['sanctioned_vessel_nor', nDays, country],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const useGlobalSanctionedInsight = (nDays: string, country: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/recent_vessel_events/sanctioned_vessels_outside_aoi?n_days=${nDays}&case_name=${country}`;
    return useQuery<InsightDashboard[]>(
        ['nor_sanctioned_insights', nDays, country],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};

export const useAOINorInsight = (nDays: string, country: string, caseName: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/recent_vessel_events/vessels_in_aoi?case_name=${caseName}&n_days=${nDays}`;
    return useQuery<InsightDashboard[]>(
        [caseName, nDays, country],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
            enabled: country === 'norway',
        }
    );
};

export const useRelevantSubscriptions = (insight_uuid: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/recent_vessel_events/find_relevant_subscriptions?uuid=${insight_uuid}`;
    return useQuery<Subscriptions[]>(
        ['relevant_subscriptions', insight_uuid],
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
        }
    );
};
